import { Router, ActivatedRoute } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  ViewChild,
  PLATFORM_ID,
  Inject,
  ContentChild,
  TemplateRef,
  Input,
} from '@angular/core';
import {
  CommonModule,
  NgOptimizedImage,
} from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { LocaleService } from '@app/locale.service';
import { LoginService } from '@app/login.service';
import { LogoComponent } from '@app/logo/logo.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { RouterModule } from '@angular/router';
import { LogoNegativeComponent } from '@app/logo-negative/logo-negative.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    AngularSvgIconModule,
    CommonModule,
    NgOptimizedImage,
    LogoComponent,
    LogoNegativeComponent
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {

  @ContentChild('headerContent')
  public headerContent?: TemplateRef<any>;

  @Input()
  public transparent = false;

  public menuOpen = new BehaviorSubject<string | null>(null);
  public menuItems = new BehaviorSubject<any[]>([]);
  public isRootPage = false;
  public isHostelPage = false;

  public bookingOptions: any;

  public locked = false;
  @Input()
  public isBackground = false;

  @ViewChild('header')
  public header!: ElementRef;

  private observer?: IntersectionObserver;
  private readonly HEADER_HEIGHT_PX = 83;
  private verticalOffset = 0;
  private scrollListener: any;

  @HostListener('window:scroll', ['$event']) onScrollEvent($event: any) {
    if (this.isHostelPage) {
      return;
    }
    if (this.isBackground) {
      return;
    }
    const verticalOffset =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    let direction;
    if (verticalOffset < this.verticalOffset) {
      direction = 'up';
    } else {
      direction = 'down';
    }

    this.verticalOffset = verticalOffset;

    if (direction === 'up') {
      this.header.nativeElement.classList.remove('unpinned');
      this.header.nativeElement.classList.add('pinned');
    } else if (this.verticalOffset > this.HEADER_HEIGHT_PX) {
      this.header.nativeElement.classList.remove('pinned');
      this.header.nativeElement.classList.add('unpinned');
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private localeService: LocaleService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
  }

  public menuConfig: { [key: string]: any[] } = {
    support: [
      {
        label: 'Talk to us',
        target: ['/help'],
        fragment: 'talk-to-us',
      },
      {
        label: 'FAQ',
        target: ['/help'],
      },
    ],
    resources: [
      {
        label: 'Blog',
        target: ['/blog'],
      },
      {
        label: 'Press',
        target: ['/blog'],
      },
    ],
    'about-us': [
      {
        label: 'Our story',
        target: ['/about-us'],
      },
      {
        label: 'Careers',
        target: ['/careers'],
      },
    ],
    'work-with-us': [
      {
        label: 'Hostel signup',
        target: ['/hostel-signup'],
      },
      {
        label: 'Ambassador program',
        target: ['/ambassador-program'],
      },
      {
        label: 'Affiliate program',
        target: ['/affiliate-program'],
      },
    ],
  };

  public openMenu(category: string) {
    this.menuOpen.next(category);
    this.menuItems.next(this.menuConfig[category]);
  }

  public closeMenu(overrideLock = false) {
    if (this.locked) {
      if (overrideLock) {
        this.menuOpen.next(null);
        this.locked = false;
        this.menuItems.next([]);
      }
    } else {
      this.menuOpen.next(null);
      this.locked = false;
      this.menuItems.next([]);
    }
  }

  public login() {
    this.loginService.showLogin();
  }

  public lock() {
    this.locked = true;
  }

  public unlock() {
    this.locked = false;
    this.closeMenu();
  }

  public selectLocale() {
    this.localeService.showLocaleModal();
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
